import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { DataTable, OptionAutocomplete, OptionSelect } from '../../../../ui';
import { colors } from '../../../../theme/tokens';
import {
  filterManufacturersByPlatform,
  filterOsesByPlatform,
} from '../../../../services/deviceService';
import actions from '../../../../redux/actions';
import { DevicePlatform } from '../../../../types/DevicePlatform';
import { OperationStatus } from '../../../../types/OperationStatus';
import { getPlatformPrettyName } from '../../../../services/deviceService';

const mapStateToProps = (state) => {
  return {
    addDeviceStatus: state.addDeviceStatus,
    user: state.user,
    manufacturers: state.devices.manufacturers,
    models: state.devices.models,
    oses: state.devices.oses,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    addDevice: (data) => dispatch(actions.addDevice(data)),
    deleteDevice: (data) => dispatch(actions.deleteDevice(data)),
  };
};

const platformOptions: any[] = Object.entries(DevicePlatform).reduce((result, [key, val]) => {
  if (val !== DevicePlatform.Desktop && val !== DevicePlatform.WindowsDesktop && val !== DevicePlatform.WindowsLaptop ) {
    result.push({
      text: getPlatformPrettyName(val),
      key: DevicePlatform[key],
      value: DevicePlatform[key],
    });
  }

  return result;
}, [] as any[]);

class Devices extends Component<any> {
  public state: any;

  constructor(props) {
    super(props);
    this.state = {
      newPlatformId: '',
      newManufacturerId: '',
      newModelId: '',
      newOsId: '',
      manufacturers: [],
      models: [],
      oses: [],
    };
  }

  onChange = (event, data) => {
    switch (data.name) {
      case 'newPlatformId': {
        const platformId = data.value;
        const manufacturers = filterManufacturersByPlatform(
          platformId,
          this.props.manufacturers,
          this.props.models
        );

        let newManufacturerId = '';
        let models: any[] = [];
        let newModelId = '';   

        // TODO maybe same for models, OSes etc
        if (manufacturers.length === 1) {
          newManufacturerId = manufacturers[0].id;
          models = this.props.models
            .filter((el) => el.platformId === data.value)
            .filter((el) => el.manufacturerId === newManufacturerId);
            
          if( models.length === 1 ) {            
            newModelId = models[0].id;            
          }
        }

        const oses = filterOsesByPlatform(data.value, this.props.oses);

        const newState: Record<string, any> = {
          ...this.state,
          newPlatformId: data.value,
          newManufacturerId,
          newModelId,
          newOsId: '',
          manufacturers,
          models,
          oses,
        };

        return this.setState(newState);
      }

      case 'newManufacturerId': {
        const models = this.props.models
          .filter((el) => el.platformId === this.state.newPlatformId)
          .filter((el) => el.manufacturerId === data.value);

        let newModelId = '';

        if( models.length === 1 ) {            
          newModelId = models[0].id;
        }

        const newState: Record<string, any> = {
          ...this.state,
          newManufacturerId: data.value,
          newModelId,
          newOsId: '',
          models,
        };

        return this.setState(newState);
      }

      case 'newModelId':
        return this.setState({
          ...this.state,
          newModelId: data.value,
          newOsId: '',
        });

      case 'newOsId':
        return this.setState({
          ...this.state,
          newOsId: data.value,
        });

      default:
        return;
    }
  };

  onAddDevice = () => {
    // TODO send only modelId and osId, populate platformId, manufacturerId and name on the server side
    this.props.addDevice({
      platformId: this.state.newPlatformId,
      manufacturerId: this.state.newManufacturerId,
      modelId: this.state.newModelId,
      osId: this.state.newOsId,
    });
  };

  onDeleteDevice = (event, data) => {
    this.props.deleteDevice({
      modelId: data['data-device-model-id'],
      osId: data['data-device-os-id'],
    });
  };

  isDeviceAlreadyExists = (device) => {
    const result = this.props.user.devices.find(
      (el) => el.modelId === device.modelId && el.osId === device.osId
    );

    if (result) {
      return true;
    }

    return false;
  };

  render() {
    const user = this.props.user;

    const toOptions = (items: any[]) => items.map((el) => ({ key: el.id, value: el.id, text: el.name }));
    const vendorOptions = toOptions(this.state.manufacturers);
    const modelOptions = toOptions(this.state.models);
    const osOptions = toOptions(this.state.oses);

    const processing = this.props.addDeviceStatus.status === OperationStatus.processing;
    const alreadyAdded = this.isDeviceAlreadyExists({ modelId: this.state.newModelId, osId: this.state.newOsId });
    const disabled =
      processing ||
      !this.state.newPlatformId ||
      !this.state.newManufacturerId ||
      !this.state.newModelId ||
      !this.state.newOsId ||
      alreadyAdded;

    // TODO ensure the default values for manufacturers, models and oses are always set
    const manufacturers = this.props.manufacturers || [];
    const models = this.props.models || [];
    const oses = this.props.oses || [];

    const change = (name: string) => (value: any) => this.onChange(null, { name, value: value ?? '' });

    const rows: any[] = user.devices.map((el) => {
      const model = models.find((mod) => mod.id === el.modelId);
      const manufacturerId = model ? model.manufacturerId : -1;
      const manufacturer = manufacturers.find((man) => man.id === manufacturerId);
      const os = oses.find((item) => item.id === el.osId);

      return {
        modelId: el.modelId,
        osId: el.osId,
        brand: manufacturer ? manufacturer.name : `Unknown manufacturer ${manufacturerId}`,
        model: model ? model.name : `Unknown model ${el.modelId}`,
        os: os ? os.name : `Unknown OS ${el.osId}`,
      };
    });

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1.5 }}>Add device</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr) auto' }, gap: 1.5, alignItems: 'start' }}>
            <OptionSelect label="Device type" options={platformOptions} value={this.state.newPlatformId} onValueChange={change('newPlatformId')} />
            <OptionAutocomplete label="Brand" options={vendorOptions} value={this.state.newManufacturerId} onValueChange={change('newManufacturerId')}
              disabled={!this.state.newPlatformId || vendorOptions.length === 1} />
            <OptionAutocomplete label="Model" options={modelOptions} value={this.state.newModelId} onValueChange={change('newModelId')}
              disabled={!this.state.newManufacturerId || modelOptions.length === 1} />
            <OptionAutocomplete label="OS" options={osOptions} value={this.state.newOsId} onValueChange={change('newOsId')}
              disabled={!this.state.newPlatformId} />
            <Button variant="contained" startIcon={<AddIcon />} disabled={disabled} onClick={this.onAddDevice} sx={{ height: 56 }}>
              {processing ? 'Adding…' : 'Add'}
            </Button>
          </Box>
          {alreadyAdded && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This device is already in your list.</Typography>
          )}
        </Box>

        <Box>
          <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: colors.titleBlue, mb: 1 }}>My devices</Typography>
          <DataTable
            rows={rows}
            rowKey={(row) => `${row.modelId}-${row.osId}`}
            emptyText="You haven't added any devices yet"
            columns={[
              { key: 'brand', label: 'Brand', render: (row) => row.brand },
              { key: 'model', label: 'Model', render: (row) => row.model },
              { key: 'os', label: 'OS', render: (row) => row.os },
              {
                key: 'actions',
                align: 'right',
                render: (row) => (
                  <Button size="small" color="error" onClick={() => this.onDeleteDevice(null, { 'data-device-model-id': row.modelId, 'data-device-os-id': row.osId })}>
                    Delete
                  </Button>
                ),
              },
            ]}
          />
        </Box>
      </Box>
    );
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Devices));
